import { useState } from "react";
import { Typography as T, Tooltip, styled } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentBox from "../../../components/ContentBox";
import { ReactComponent as ArrowsIcon } from "../../../assets/icons/adminDashboard/arrows.svg";
import Button from "../../../components/Button";
import { colors } from "../../../styles";
import InviteNewAdminModal from "../../../components/InviteNewAdminModal";
import { TeamMemberSetting } from "../../../types";
import useInactivateTeamMember from "../../../hooks/mutations/admin/useInactivateTeamMember";

export const PageContainer = styled("div")(`
    width: 100%;
    height: 100%;
`);

export const HeaderGradient = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => {
  const Gradient = styled("div")({
    height: "143px",
    padding: "8px 32px",
    width: "100%",
    background:
      "linear-gradient(88.81deg, #EFEFFD 0%, #DBF2FC 51.04%, #FFF4E3)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  });

  return (
    <div>
      <Gradient>
        <T variant="headingMd">{title}</T>
        <T variant="lgReg">{subtitle}</T>
      </Gradient>
    </div>
  );
};

interface BoxHeaderProps {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  description?: string;
}
export const BoxHeader = ({ Icon, description }: BoxHeaderProps) => {
  return (
    <div
      className="flex flex-row"
      style={{ minHeight: 48, justifyContent: "space-between" }}
    >
      {Icon ? <Icon height={30} width={30} style={{ flexShrink: 0 }} /> : null}
      {description && (
        <div style={{}}>
          <Tooltip title={description} arrow>
            <InfoOutlinedIcon sx={{ color: colors.gray_400 }} />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const ContentContainer = ({
  children,
  alignCenter,
}: {
  children: React.ReactNode;
  alignCenter?: boolean;
}) => {
  return (
    <div
      className={`flex flex-col m-32 max-content-width mb-64 ${
        alignCenter ? "items-center" : ""
      }`}
    >
      {children}
    </div>
  );
};

export const StatBox = ({
  children,
  Icon,
  title,
  description,
}: {
  title: string;
  Icon?: BoxHeaderProps["Icon"];
  children?: React.ReactNode;
  description?: string;
}) => {
  return (
    <ContentBox
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "20px 20px",
      }}
    >
      <BoxHeader Icon={Icon} description={description} />
      <div style={{ marginTop: "auto" }}>
        <T variant="smMed">{title}</T>
        {children}
      </div>
    </ContentBox>
  );
};

export const StatValue = ({
  value,
  description,
}: {
  value: number | string;
  description?: string;
}) => {
  return (
    <div>
      <T variant="mdMed" color={colors.gray_600}>
        {description}
      </T>
      <T variant="headingLg" style={{ marginBottom: 0 }}>
        {value}
      </T>
    </div>
  );
};

export const InviteProgramAdminsBox = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <ContentBox style={{ marginTop: 0 }}>
      <BoxHeader Icon={ArrowsIcon} />
      <T variant="lgMed">Invite Program Admins</T>
      <T variant="mdReg">
        Program administrators can view employee information, export data, and
        update program settings.
      </T>
      <div className="mt-16" style={{ width: "150px" }}>
        <Button
          size="small"
          sx={{ margin: 0 }}
          onClick={() => setShowModal(true)}
        >
          Invite
        </Button>
      </div>
      <InviteNewAdminModal
        modalStatus={showModal}
        closeModal={() => setShowModal(false)}
      />
    </ContentBox>
  );
};

export const SettingsTableActionsMenu = ({
  data,
}: {
  data: TeamMemberSetting;
}) => {
  const { mutate: inactivateAccount } = useInactivateTeamMember({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleInactivateClick = () => {
    inactivateAccount({ user_to_inactivate: data.user.id });
  };

  return (
    <div>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleButtonClick}
        sx={{ margin: 0 }}
        variant="text"
        endIcon={<KeyboardArrowDownIcon />}
      >
        Actions
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        elevation={1}
      >
        <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <BlockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText onClick={handleInactivateClick}>
            Disable account
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};
